import { Button, Card, CardContent, Divider, Typography } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import UserApi from '../../../api/UserApi'
import useAuth from '../../../hooks/useAuth'
import NewUserDialog, { NewUserForm } from './NewUserDialog'
import UsersGrid from '../../../components/entities/UsersGrid'
import { Stack } from '@mui/system'

import UsersFilter from './UsersFilter'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { PaginatedResponse, UserListItem, UserListSortField } from '@finance-lab/utils/types'

const userApi = new UserApi()

const DEFAULT_PAGE_SIZE = 25

const defaultFilterState = { userCategory: 'standard' } as const

function AdminUsersView() {
    const { isAuthenticated } = useAuth()
    const [pageInfo, setPageInfo] = useState<PaginatedResponse<UserListItem>>({ data: [], totalCount: 0 })
    const [loading, setLoading] = useState(true)
    const [filterState, setFilterState] = useState(defaultFilterState)
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE })
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }])
    const [open, setOpen] = useState(false)
    const [quickFilterSearch, setQuickFilterSearch] = useState('')
    const [search, setSearch] = useState('')
    const triggerButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            const nextSearch = quickFilterSearch.trim()
            setSearch((prev) => {
                if (prev === nextSearch) {
                    return prev
                }
                setPaginationModel((model) => ({ ...model, page: 0 }))
                return nextSearch
            })
        }, 300)
        return () => window.clearTimeout(timeout)
    }, [quickFilterSearch])

    const loadUsers = useCallback(async () => {
        if (!isAuthenticated) {
            return
        }
        setLoading(true)
        try {
            const primarySort = sortModel[0]
            const response = await userApi.adminFindUsers({
                paginationModel,
                userCategory: filterState.userCategory,
                search: search || undefined,
                sortField: (primarySort?.field as UserListSortField) ?? 'name',
                sortDirection: primarySort?.sort ?? 'asc',
            })
            setPageInfo(response)
        } finally {
            setLoading(false)
        }
    }, [filterState, isAuthenticated, paginationModel, search, sortModel])

    useEffect(() => {
        void loadUsers()
    }, [loadUsers])

    const handleFilterChange = (newFilterState: typeof filterState) => {
        setFilterState(newFilterState)
        setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }

    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel)
        setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }

    useEffect(() => {
        if (!loading) {
            document.title = `Användare (${pageInfo.totalCount})`
        }
    }, [loading, pageInfo.totalCount])

    const handleOpenDialog = () => {
        setOpen(true)
    }

    const handleCloseDialog = () => {
        setOpen(false)
        if (triggerButtonRef.current) {
            triggerButtonRef.current.focus()
        }
    }

    const handleCreateUser = async ({ customer, trialStartsAt, trialEndsAt, ...formData }: NewUserForm) => {
        try {
            const { userCategory, ...rest } = formData
            await userApi.createUser({
                customerId: customer?.id || null,
                ...rest,
                userCategory,
                ...(userCategory === 'trial' && trialStartsAt && trialEndsAt
                    ? { trialPeriod: { startsAt: trialStartsAt, endsAt: trialEndsAt } }
                    : {}),
            })
            await loadUsers()
            handleCloseDialog()
        } catch (error) {
            console.error('Failed to create user:', error)
            throw error
        }
    }

    const setUsers = (users: UserListItem[]) => {
        setPageInfo((prev) => ({ ...prev, data: users }))
    }

    return (
        <>
            <Helmet>
                <title>Användare</title>
            </Helmet>
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    mb: 4,
                    display: 'block',
                }}
            >
                Användare ({loading ? '...' : pageInfo.totalCount})
            </Typography>
            <Stack
                direction='row'
                spacing={2}
                sx={{
                    mb: 4,
                }}
            >
                <UsersFilter filterState={filterState} setFilterState={handleFilterChange} />
                <Divider orientation='vertical' flexItem />
                <Button variant='contained' color='primary' onClick={handleOpenDialog} ref={triggerButtonRef}>
                    Skapa ny användare
                </Button>
            </Stack>
            <Divider />
            <Card sx={{ mb: 6 }}>
                <CardContent>
                    <UsersGrid
                        users={pageInfo.data}
                        setUsers={setUsers}
                        highlightManyDevices
                        loading={loading}
                        paginationMode='server'
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        rowCount={pageInfo.totalCount}
                        sortingMode='server'
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        onRefreshPage={loadUsers}
                        quickFilterSearch={quickFilterSearch}
                        onQuickFilterSearchChange={setQuickFilterSearch}
                    />
                </CardContent>
            </Card>
            <NewUserDialog open={open} onClose={handleCloseDialog} onSubmit={handleCreateUser} />
        </>
    )
}

export default AdminUsersView
